import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowDownRight, ChevronDown } from "lucide-react";
import { useContent } from "../../hooks/useContent";

const techStack = [
    "JavaScript", "TypeScript", "React", "Next.js", "Node.js", "Python",
    "Rust", "Go", "WebGL", "Three.js", "AWS", "Docker", "Kubernetes",
    "GraphQL", "PostgreSQL", "MongoDB", "Solidity", "Tailwind CSS", "GSAP"
];

const MobileServices = ({ services, showImages }) => {
    const [activeIndex, setActiveIndex] = useState(null);

    const toggle = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    return (
        <div className="flex flex-col border-t border-white/20">
            {services.map((service, index) => (
                <div key={index} className="border-b border-white/20 py-5 md:py-6">
                    <button
                        onClick={() => toggle(index)}
                        className="w-full flex justify-between items-start text-left group"
                    >
                        <div className="flex-1 pr-4">
                            <span className="font-mono text-gray-600 text-[10px] md:text-xs block mb-2">0{index + 1}</span>
                            <h4 className={`text-2xl md:text-4xl font-display font-bold transition-colors duration-300 ${activeIndex === index ? 'text-accent' : 'text-white'}`}>
                                {service.title}
                            </h4>
                            <p className="text-[10px] md:text-xs text-gray-500 font-mono uppercase tracking-widest mt-2">{service.desc}</p>
                        </div>
                        <ChevronDown className={`w-5 h-5 md:w-6 md:h-6 text-gray-500 transition-transform duration-300 mt-2 ${activeIndex === index ? 'rotate-180 text-accent' : ''}`} />
                    </button>

                    <AnimatePresence>
                        {activeIndex === index && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="overflow-hidden"
                            >
                                <div className="pt-6 pb-2">
                                    <p className="text-gray-300 leading-relaxed mb-6 border-l-2 border-accent pl-4 text-sm md:text-base">
                                        {service.details}
                                    </p>

                                    {service.hasTech && (
                                        <div className="mb-6">
                                            <p className="text-[10px] md:text-xs font-mono uppercase text-accent mb-3 tracking-widest">Tech Stack</p>
                                            <div className="flex flex-wrap gap-2">
                                                {techStack.map((tech, i) => (
                                                    <span key={i} className="px-2 py-1 border border-white/10 text-[10px] md:text-xs font-mono text-gray-400 uppercase">
                                                        {tech}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {showImages && service.src && (
                                        <div className="overflow-hidden rounded-sm">
                                            <img
                                                src={service.src}
                                                alt={service.title}
                                                className="w-full h-48 md:h-64 object-cover"
                                            />
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            ))}
        </div>
    );
};

const DesktopServices = ({ services, showImages }) => {
    const [hoveredIndex, setHoveredIndex] = useState(null);

    return (
        <div className="flex flex-col border-t border-white/20">
            {services.map((service, index) => (
                <div
                    key={index}
                    className="group relative border-b border-white/20 cursor-pointer overflow-hidden transition-colors"
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                >
                    <div className="py-14 flex flex-row justify-between items-center z-10 relative gap-6 px-2 group-hover:px-6 transition-all duration-500 ease-out">
                        <div className="flex items-center gap-8">
                            <span className="font-mono text-gray-600 text-sm">0{index + 1}</span>
                            <h4 className="text-7xl font-display font-bold text-gray-400 group-hover:text-accent transition-colors duration-300">
                                {service.title}
                            </h4>
                        </div>

                        <div className="flex items-center gap-16">
                            <p className="text-lg font-mono text-gray-500 uppercase tracking-widest group-hover:text-white transition-colors duration-300">
                                {service.desc}
                            </p>
                            <div className={`p-3 rounded-full border border-white/10 group-hover:border-accent group-hover:bg-accent transition-all duration-500`}>
                                <ArrowDownRight className={`w-6 h-6 text-gray-500 group-hover:text-black transition-transform duration-500 ${hoveredIndex === index ? "-rotate-90" : ""}`} />
                            </div>
                        </div>
                    </div>

                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{
                            height: hoveredIndex === index ? "auto" : 0,
                            opacity: hoveredIndex === index ? 1 : 0
                        }}
                        transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
                        className="overflow-hidden"
                    >
                        <div className="pb-16 pt-4 grid grid-cols-12 gap-12 px-6">
                            <div className={`${showImages ? 'col-span-5' : 'col-span-12'} order-1 flex flex-col justify-between`}>
                                <div>
                                    <p className="text-2xl text-gray-300 font-light leading-relaxed mb-10 border-l-2 border-accent pl-6">
                                        {service.details}
                                    </p>

                                    {service.hasTech && (
                                        <div className="mb-10">
                                            <p className="text-xs font-mono uppercase text-accent mb-4 tracking-widest">Tech Stack</p>
                                            <div className="flex flex-wrap gap-2">
                                                {techStack.map((tech, i) => (
                                                    <span key={i} className="px-3 py-1 border border-white/10 text-xs font-mono text-gray-400 uppercase hover:border-accent hover:text-white transition-colors">
                                                        {tech}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <button className="text-accent uppercase tracking-widest font-bold text-sm hover:text-white transition-colors flex items-center gap-4 group/btn w-fit">
                                    Explore Service
                                    <span className="w-12 h-px bg-accent group-hover/btn:w-24 transition-all duration-300"></span>
                                </button>
                            </div>

                            {showImages && service.src && (
                                <div className="col-span-7 order-2 h-[500px] w-full relative overflow-hidden bg-gray-900">
                                    <div className="absolute inset-0 bg-accent/10 mix-blend-overlay z-10"></div>
                                    <img
                                        src={service.src}
                                        alt={service.title}
                                        className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-1000 scale-110 group-hover:scale-100"
                                    />
                                </div>
                            )}
                        </div>
                    </motion.div>
                </div>
            ))}
        </div>
    );
};

const Services = () => {
    const [isMobile, setIsMobile] = useState(true);
    const { content } = useContent("services");

    // Default Services (Hardcoded Fallback)
    const defaultServices = [
        { title: "Development", desc: "Engineering the Future", details: "We build scalable, high-performance applications...", src: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?q=80&w=2070&auto=format&fit=crop", hasTech: true },
        { title: "Design", desc: "UI/UX, Motion, Branding", details: "Immersive interfaces that blend aesthetics...", src: "https://images.unsplash.com/photo-1626785774573-4b799314346d?q=80&w=2070&auto=format&fit=crop" },
        { title: "Strategy", desc: "Product, Market, Growth", details: "Data-driven insights to position your brand...", src: "https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=2070&auto=format&fit=crop" },
        { title: "Content", desc: "Copywriting, SEO, Video", details: "Compelling narratives that resonate...", src: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?q=80&w=2070&auto=format&fit=crop" }
    ];

    const services = content?.services || defaultServices;
    const showImages = content?.showImages !== false; // Default to true if undefined

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 1024);
        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    return (
        <section id="expertise" className="py-20 md:py-40 px-4 md:px-12 bg-black relative z-10">
            <div className="max-w-7xl mx-auto">
                <div className="mb-12 md:mb-32 flex flex-col md:flex-row justify-between items-end">
                    <div>
                        <h2 className="text-xs md:text-sm font-bold uppercase tracking-widest text-gray-500 mb-4 md:mb-6 flex items-center gap-3">
                            <span className="w-2 h-2 bg-accent rounded-full animate-pulse"></span>
                            Our Expertise
                        </h2>
                        <h3 className="text-4xl sm:text-5xl md:text-[7rem] font-display text-white leading-[0.9] md:leading-[0.85] tracking-tighter">
                            CORE <br /> <span className="text-stroke hover:text-accent transition-colors duration-500">CAPABILITIES</span>
                        </h3>
                    </div>
                    <p className="hidden md:block text-gray-400 max-w-sm text-right font-mono text-xs uppercase tracking-wider leading-relaxed">
                        / 01 — Full Cycle Production <br />
                        / 02 — Digital Transformation <br />
                        / 03 — Brand Acceleration
                    </p>
                </div>

                {isMobile ?
                    <MobileServices services={services} showImages={showImages} /> :
                    <DesktopServices services={services} showImages={showImages} />
                }
            </div>
        </section>
    );
};

export default Services;
